import { Box, Heading, Text, VStack } from '@gluestack-ui/themed';
import { ApiError } from '@/shared/types/api.types';
import { AppIcon } from '../ui/AppIcon/AppIcon';
import { Button } from '../ui/Button/Button';
import { ICON_TILE_SIZE } from '../../styles/tokens';

export interface ErrorStateProps {
  readonly error: unknown;
  readonly onRetry?: () => void;
  readonly testID?: string;
}

function describe(error: unknown): { title: string; message: string; canRetry: boolean } {
  if (error instanceof ApiError) {
    return {
      title: titleFor(error),
      message: error.message,
      canRetry: error.isRetryable,
    };
  }

  return {
    title: 'Something went wrong',
    message: error instanceof Error ? error.message : 'An unexpected error occurred.',
    canRetry: true,
  };
}

function titleFor(error: ApiError): string {
  switch (error.kind) {
    case 'network':
    case 'timeout':
      return 'Connection problem';
    case 'unauthorized':
      return 'Session expired';
    case 'forbidden':
      return 'Access denied';
    case 'notFound':
      return 'Not found';
    case 'validation':
      return 'Invalid request';
    case 'conflict':
      return 'Conflicting change';
    case 'server':
      return 'Server error';
    case 'unknown':
      return 'Something went wrong';
  }
}

/**
 * Failure state for a region whose data could not load.
 *
 * Retry is only offered for errors where retrying can help — re-requesting a 403 just
 * fails again and teaches users to distrust the button.
 */
export function ErrorState({ error, onRetry, testID }: ErrorStateProps): React.JSX.Element {
  const { title, message, canRetry } = describe(error);

  return (
    <VStack
      role="alert"
      alignItems="center"
      justifyContent="center"
      py="$6"
      px="$4"
      space="sm"
      testID={testID}
    >
      <Box
        width={ICON_TILE_SIZE}
        height={ICON_TILE_SIZE}
        borderRadius="$md"
        bg="$error50"
        $dark-bg="$backgroundDark800"
        alignItems="center"
        justifyContent="center"
        mb="$1"
      >
        <Text color="$error600" $dark-color="$error400">
          <AppIcon name="alert" size={16} />
        </Text>
      </Box>
      <Heading size="sm" textAlign="center">
        {title}
      </Heading>
      <Text size="sm" textAlign="center" color="$textLight600" $dark-color="$textDark400">
        {message}
      </Text>
      {onRetry !== undefined && canRetry ? (
        <Box mt="$2">
          <Button variant="outline" size="sm" onPress={onRetry}>
            Try again
          </Button>
        </Box>
      ) : null}
    </VStack>
  );
}
